/* src/utils/pwaUtils.js */

const IOS_ADD_TO_HOME_SCREEN_PROMPT_SEEN_KEY = 'hasSeenAddToHomeScreenPrompt_v1'; // Add versioning
// *** General App Visit Count Key ***
const APP_VISIT_COUNT_KEY = 'appVisitCount_v1';
const ANDROID_A2HS_MODAL_DISMISSED_KEY = 'androidA2HSModalDismissed_v1';


/**
 * Checks if the current environment is iOS Safari and the app is not running in standalone mode.
 * @returns {boolean} True if potentially running in iOS Safari browser tab, false otherwise.
 */
export const isPotentiallyIOSWebApp = () => { // Export this
    // Basic check for iPhone, iPad, iPod
    // Ensure window and navigator are available (client-side check)
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    // Check if running in standalone mode (already added to home screen)
    const isInStandaloneMode = 'standalone' in window.navigator && window.navigator.standalone;

    // Check if likely Safari (heuristic: is iOS and not explicitly another browser like Chrome/Firefox on iOS)
    // Note: This isn't foolproof as user agents can be spoofed or changed.
    const isSafari = isIOS && !/CriOS|FxiOS|EdgiOS/.test(navigator.userAgent);

    // Return true only if it's iOS Safari and *not* already running standalone
    return isSafari && !isInStandaloneMode;
}; // *** Corrected: Ensure semicolon here if needed, or just proper function closing ***

/**
 * Checks if the user is on iOS Safari, the app isn't standalone,
 * and if they haven't dismissed the prompt before (checks localStorage).
 * Note: This function is now primarily used by MainAppLayout which adds the visit count check.
 * @returns {boolean} True if the prompt should potentially be shown (before visit check), false otherwise.
 */
export const shouldShowAddToHomeScreenPrompt = () => {
    // ** Note: Visit count check is now handled in MainAppLayout useEffect **
    if (!isPotentiallyIOSWebApp()) {
        return false; // Not iOS Safari or already standalone
    }

    try {
        const hasSeenPrompt = localStorage.getItem(IOS_ADD_TO_HOME_SCREEN_PROMPT_SEEN_KEY);
        return !hasSeenPrompt; // Show if the flag is NOT set in localStorage
    } catch (error) {
        console.error("Error accessing localStorage for A2HS prompt:", error);
        return false; // Don't show if localStorage fails
    }
};

/**
 * Marks the Add to Home Screen prompt as seen by setting a flag in localStorage.
 */
export const markAddToHomeScreenPromptSeen = () => {
    try {
        localStorage.setItem(IOS_ADD_TO_HOME_SCREEN_PROMPT_SEEN_KEY, 'true');
    } catch (error) {
        console.error("Error setting localStorage for A2HS prompt:", error);
    }
};

/**
 * (Optional) Clears the 'seen' flag for testing purposes.
 */
export const resetAddToHomeScreenPromptSeen = () => {
    try {
      localStorage.removeItem(IOS_ADD_TO_HOME_SCREEN_PROMPT_SEEN_KEY);
      console.log("A2HS prompt 'seen' flag cleared.");
    } catch (error) {
      console.error("Error clearing localStorage for A2HS prompt:", error);
    }
  };


// --- Platform/Visit Utilities ---

/**
 * Checks if the current user agent string likely belongs to an Android device.
 * @returns {boolean} True if likely Android, false otherwise.
 */
export const isAndroid = () => {
    if (typeof navigator === 'undefined') return false;
    return /android/i.test(navigator.userAgent);
};

/**
 * Gets the current app visit count from localStorage.
 * @returns {number} The visit count (defaults to 0).
 */
export const getAppVisitCount = () => {
    try {
        const count = localStorage.getItem(APP_VISIT_COUNT_KEY);
        return count ? parseInt(count, 10) : 0;
    } catch (error) {
        console.error("Error getting app visit count:", error);
        return 0;
    }
};

/**
 * Increments the app visit count in localStorage.
 */
export const incrementAppVisitCount = () => {
    try {
        const currentCount = getAppVisitCount();
        localStorage.setItem(APP_VISIT_COUNT_KEY, (currentCount + 1).toString());
        console.log(`[Visit Counter] App visit count incremented to ${currentCount + 1}`);
    } catch (error) {
        console.error("Error incrementing app visit count:", error);
    }
};

/**
 * Checks if the user has previously dismissed the Android A2HS *modal*.
 * @returns {boolean} True if the dismissal flag is set, false otherwise.
 */
export const hasDismissedAndroidA2HSModal = () => {
    try {
        return !!localStorage.getItem(ANDROID_A2HS_MODAL_DISMISSED_KEY);
    } catch (error) {
        console.error("Error checking Android A2HS modal dismissal:", error);
        return false; // Default to not dismissed if error occurs
    }
};

/**
 * Marks the Android A2HS *modal* as dismissed in localStorage.
 */
export const markAndroidA2HSModalDismissed = () => {
    try {
        localStorage.setItem(ANDROID_A2HS_MODAL_DISMISSED_KEY, 'true');
        console.log("[PWA Install] Android A2HS modal marked as dismissed.");
    } catch (error) {
        console.error("Error marking Android A2HS modal dismissed:", error);
    }
};

/**
 * (Optional) Clears PWA related flags for testing.
 */
export const resetPWAFlags = () => {
    try {
      localStorage.removeItem(APP_VISIT_COUNT_KEY);
      localStorage.removeItem(ANDROID_A2HS_MODAL_DISMISSED_KEY);
      localStorage.removeItem(IOS_ADD_TO_HOME_SCREEN_PROMPT_SEEN_KEY);
      console.log("App visit count, Android modal dismissed, and iOS prompt seen flags cleared.");
    } catch (error) {
      console.error("Error clearing PWA flags:", error);
    }
};

/**
 * Checks if the app is currently running in standalone mode (installed PWA).
 * Works for both iOS (navigator.standalone) and standard PWA display-mode query.
 * @returns {boolean} True if running standalone, false otherwise.
 */
export const isStandaloneMode = () => {
   if (typeof window === 'undefined' || typeof navigator === 'undefined') {
       return false;
   }

   // Check iOS specific property
   if ('standalone' in navigator && navigator.standalone) {
     return true;
   }

   // Check standard PWA display-mode media query
   if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
     return true;
   }

   return false;
};